import { useMemo, useState } from 'react';
import { CheckCheck, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { useAcceptNetworkMatches, useNetworkMap, useSetNetworkMapping, type NetMapRow } from '@/api/network';
import { cn } from '@/lib/utils';

/**
 * Admin → Network → Mapping. Link each Sophos tenant and UniFi site to an Autotask company.
 * Suggestions come from server/network/map.ts (contact domains, client codes in console names,
 * WAN IP vs Datto RMM); nothing is mapped until an admin accepts it.
 */

type View = 'review' | 'all';
type Source = 'all' | 'sophos' | 'unifi';

export default function AdminNetworkMappingPage() {
  const { data, isLoading, error, isFetching } = useNetworkMap();
  const accept = useAcceptNetworkMatches();
  const [view, setView] = useState<View>('review');
  const [source, setSource] = useState<Source>('all');
  const [q, setQ] = useState('');

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data ?? []).filter((r) =>
      (source === 'all' || r.source === source)
      && (view === 'all' || !r.mapping)
      && (!s || r.label.toLowerCase().includes(s) || (r.detail ?? '').toLowerCase().includes(s) || (r.mapping?.companyName ?? r.suggestion?.companyName ?? '').toLowerCase().includes(s)));
  }, [data, view, source, q]);

  const strong = (data ?? []).filter((r) => !r.mapping && r.suggestion?.strength === 'strong');
  const unmapped = (data ?? []).filter((r) => !r.mapping).length;

  async function acceptStrong() {
    try {
      const r = await accept.mutateAsync(strong.map((x) => ({ source: x.source, id: x.id, companyId: x.suggestion!.companyId, companyName: x.suggestion!.companyName })));
      toast.success(`Mapped ${r.saved} strong match${r.saved === 1 ? '' : 'es'}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">Network mapping {isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Link Sophos tenants and UniFi sites to Autotask companies. Suggestions use contact email domains, the client code in console names and
            WAN IPs seen by Datto RMM — review before accepting.
          </p>
        </div>
        {strong.length > 0 && (
          <Button size="sm" onClick={acceptStrong} disabled={accept.isPending}>
            <CheckCheck className="mr-1.5 h-3.5 w-3.5" /> Accept {strong.length} strong match{strong.length === 1 ? '' : 'es'}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={view} onChange={setView} options={[['review', `Needs review · ${unmapped}`], ['all', `All · ${data?.length ?? 0}`]]} />
        <Tabs value={source} onChange={setSource} options={[['all', 'All'], ['sophos', 'Sophos'], ['unifi', 'UniFi']]} />
        <SearchInput withIcon placeholder="Name, IP or company" className="w-64" value={q} onValueChange={setQ} />
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading tenants and sites…</div>
      ) : error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Failed to load'}</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="w-[30%] px-4 py-2.5">Sophos tenant / UniFi site</th>
                <th className="w-[26%] px-4 py-2.5">Autotask company</th>
                <th className="px-4 py-2.5">Suggested match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.map((r) => <MapRow key={`${r.source}:${r.id}`} row={r} />)}
              {rows.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-sm text-muted-foreground">{view === 'review' && !q ? 'Everything is mapped or ignored.' : 'Nothing matches.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Tabs<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return (
    <div className="flex gap-1 rounded-lg bg-muted p-1">
      {options.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={cn('rounded-md px-3 py-1 text-xs font-medium', value === v ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>{label}</button>
      ))}
    </div>
  );
}

function MapRow({ row }: { row: NetMapRow }) {
  const set = useSetNetworkMapping();
  const [editing, setEditing] = useState(false);
  const { mapping, suggestion } = row;

  const save = (value: Parameters<typeof set.mutate>[0]['value'], done?: string) =>
    set.mutate({ source: row.source, id: row.id, value }, {
      onSuccess: () => { setEditing(false); if (done) toast.success(done); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save'),
    });

  return (
    <tr className={cn('align-top hover:bg-muted/30', set.isPending && 'opacity-60')}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase', row.source === 'sophos' ? 'bg-info-soft text-info' : 'bg-brand-soft text-brand')}>{row.source === 'sophos' ? 'Sophos' : 'UniFi'}</span>
          <span className="font-medium">{row.label}</span>
        </div>
        {row.detail && <div className="mt-0.5 text-xs text-muted-foreground">{row.detail}</div>}
      </td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <CompanySearch value={null} onChange={(c: CompanyOption | null) => { if (c) save({ companyId: c.id, companyName: c.name }); }} placeholder="Search Autotask…" className="flex-1" />
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setEditing(false)} aria-label="Cancel"><X className="h-3.5 w-3.5" /></Button>
          </div>
        ) : mapping?.ignored ? <span className="text-xs italic text-muted-foreground">Ignored</span>
          : mapping ? <><div className="font-medium">{mapping.companyName}</div><div className="text-xs text-muted-foreground">#{mapping.companyId} · {mapping.mappedBy.split('@')[0]}</div></>
          : <span className="text-xs text-muted-foreground">Not mapped</span>}
        {!editing && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
            <button type="button" className="text-muted-foreground hover:text-foreground hover:underline" onClick={() => setEditing(true)}>{mapping && !mapping.ignored ? 'Change' : 'Choose company'}</button>
            {!mapping?.ignored && <button type="button" className="text-muted-foreground hover:text-foreground hover:underline" onClick={() => save({ ignored: true }, `${row.label} ignored`)}>Ignore</button>}
            {mapping && <button type="button" className="text-muted-foreground hover:text-destructive hover:underline" onClick={() => save(null)}>Clear</button>}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {suggestion && (!mapping || (!mapping.ignored && mapping.companyId !== suggestion.companyId)) ? (
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium">{suggestion.companyName}</span>
                <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-semibold', suggestion.strength === 'strong' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning')}>{suggestion.strength}</span>
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">{suggestion.reasons.join(' · ')}</div>
            </div>
            <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs" onClick={() => save({ companyId: suggestion.companyId, companyName: suggestion.companyName }, `${row.label} → ${suggestion.companyName}`)}>Accept</Button>
          </div>
        ) : !mapping ? <span className="text-xs text-muted-foreground">No suggestion — choose a company or ignore (e.g. staff home networks).</span> : null}
      </td>
    </tr>
  );
}
